import type React from "react";
import type { FlowNode } from "./types";
import { BLOCK_W, BLOCK_H } from "./types";

// Visual + colour palette per block kind.
const PALETTE: Record<FlowNode["kind"], { bg: string; fg: string; label: string }> = {
  start:     { bg: "#0f172a", fg: "#ffffff", label: "📞 Appel entrant" },
  play:      { bg: "#dbeafe", fg: "#0c4a6e", label: "▶ Diffuser" },
  collect:   { bg: "#fef3c7", fg: "#78350f", label: "🔢 Demander un numéro" },
  menu:      { bg: "#fde68a", fg: "#78350f", label: "☰ Menu DTMF" },
  ring:      { bg: "#bfdbfe", fg: "#1e3a8a", label: "🔔 Sonner les agents" },
  voicemail: { bg: "#fecaca", fg: "#7f1d1d", label: "📥 Répondeur" },
  hangup:    { bg: "#e2e8f0", fg: "#0f172a", label: "⏹ Raccrocher" },
};

interface BlockProps {
  id: string;
  node: FlowNode;
  /** Visible display number (#N badge). Null only transiently. */
  seq: number | null;
  x: number;
  y: number;
  isRoot: boolean;
  isSelected: boolean;
  /** Linking mode: this block is a pickable target (crosshair). */
  isLinkTarget: boolean;
  /** Linking mode: this block is the edge's source (dimmed, not pickable). */
  isLinkSource: boolean;
  greetingName: (id: string | null) => string;
  /** Short human-readable label for any other node id — used in
   *  Menu summary so the operator sees "1 → SAV" not just "1". */
  shortLabel: (id: string) => string;
  onMouseDown: (id: string, e: React.MouseEvent) => void;
  onClick: (id: string, e: React.MouseEvent) => void;
}

export function Block(props: BlockProps): React.ReactElement {
  const p = PALETTE[props.node.kind];
  const stroke = props.isSelected ? "#2563eb" : props.isRoot ? "#1e293b" : "#94a3b8";
  const strokeWidth = props.isSelected ? 3 : props.isRoot ? 2 : 1;

  // Two-line summary per kind (rendered as a foreignObject so we can
  // wrap text in HTML — SVG <text> wraps poorly).
  const subtitle = describe(props.node, props.greetingName, props.shortLabel);

  return (
    <g
      transform={`translate(${props.x}, ${props.y})`}
      onMouseDown={(e) => props.onMouseDown(props.id, e)}
      onClick={(e) => {
        e.stopPropagation();
        props.onClick(props.id, e);
      }}
      style={{
        cursor: props.isLinkTarget ? "crosshair" : "grab",
        opacity: props.isLinkSource ? 0.45 : 1,
      }}
    >
      <rect
        width={BLOCK_W}
        height={BLOCK_H}
        rx={10}
        fill={p.bg}
        stroke={props.isLinkTarget ? "#16a34a" : stroke}
        strokeWidth={props.isLinkTarget ? 2 : strokeWidth}
        strokeDasharray={props.isLinkTarget ? "6 3" : undefined}
      />
      {/* #N badge — the block's stable identity, used everywhere the
          UI refers to another block (dropdowns, edges, warnings). */}
      {props.seq != null && (
        <g transform={`translate(${BLOCK_W - 40}, -10)`}>
          <rect width={40} height={20} rx={10} fill="#0f172a" />
          <text
            x={20}
            y={14}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={11}
            fontWeight={700}
          >
            #{props.seq}
          </text>
        </g>
      )}
      <foreignObject x={12} y={10} width={BLOCK_W - 24} height={BLOCK_H - 20}>
        <div
          // @ts-expect-error xmlns is valid on inline div inside foreignObject
          xmlns="http://www.w3.org/1999/xhtml"
          style={{
            color: p.fg,
            fontFamily: "inherit",
            fontSize: 13,
            lineHeight: 1.3,
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          <div style={{ fontWeight: 600 }}>{p.label}</div>
          {subtitle && (
            <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>{subtitle}</div>
          )}
        </div>
      </foreignObject>
    </g>
  );
}

function describe(
  n: FlowNode,
  greetingName: (id: string | null) => string,
  shortLabel: (id: string) => string,
): string {
  switch (n.kind) {
    case "start": return "";
    case "play": return `🎵 ${greetingName(n.greetingId)}`;
    case "collect": return `${n.min}–${n.max} chiffres · 🎵 ${greetingName(n.greetingId)}`;
    case "menu": {
      const entries = Object.entries(n.branches).sort(([a], [b]) => a.localeCompare(b));
      if (entries.length === 0) return `🎵 ${greetingName(n.greetingId)} · (aucun choix)`;
      // Show up to three "digit → target" pairs; collapse the rest.
      const head = entries.slice(0, 3).map(([d, t]) => `${d}→${shortLabel(t)}`).join(", ");
      const more = entries.length > 3 ? `, +${entries.length - 3}` : "";
      return `${head}${more}`;
    }
    case "ring": return `${n.timeoutSec} s par agent, l'un après l'autre`;
    case "voicemail": return `🎵 ${greetingName(n.greetingId)} · ${n.silent ? "silencieux" : "+ système"}`;
    case "hangup": return "Termine l'appel";
  }
}
